import { Button } from 'antd';
import { CheckBoxCell } from '@med-fe/ui';
import { api, showErrorNotification } from '@med-fe/util';
import { FixturePhoto } from '../../../models/branded-fixture-dto';

const openFixturePhoto = async (photoUrl: string) => {
  const photoTab = window.open('', '_blank');
  if (!photoTab) {
    showErrorNotification('Allow pop-ups to view fixture photos.');
    return;
  }
  photoTab.opener = null;

  try {
    const { sasUrl } = await api.post('/fixtures/photos/sas', { photoUrl });
    if (!sasUrl) throw new Error('No photo URL was returned');
    photoTab.location.replace(sasUrl);
  } catch (error) {
    photoTab.close();
    showErrorNotification('Unable to open fixture photo.', error);
  }
};

const compareNullableText = (firstValue?: string | null, secondValue?: string | null) => {
  const firstText = firstValue?.trim();
  const secondText = secondValue?.trim();

  if (!firstText && !secondText) return 0;
  if (!firstText) return 1;
  if (!secondText) return -1;

  return firstText.localeCompare(secondText, undefined, { sensitivity: 'base' });
};

export const branded_fixtures_columns = [
  {
    title: 'Fixture Name',
    dataIndex: 'fixtureName',
    key: 'fixtureName',
    sorter: (a: { fixtureName: string }, b: { fixtureName: any }) => a.fixtureName?.localeCompare(b.fixtureName),
    sortDirections: ['ascend', 'descend'],
    width: 180,
  },
  {
    title: 'Generic',
    dataIndex: 'isGeneric',
    key: 'isGeneric',
    width: 80,
    colType: 'checkbox',
    sorter: (a: { isGeneric: boolean }, b: { isGeneric: boolean }) =>
      a.isGeneric === b.isGeneric ? 0 : a.isGeneric ? -1 : 1,
    sortDirections: ['ascend', 'descend'],
    render: (value: boolean) => <CheckBoxCell value={value} />,
  },
  {
    title: 'Product Vendor',
    dataIndex: 'vendorName',
    key: 'vendorName',
    sorter: (a: { vendorName: string | null }, b: { vendorName: string | null }) =>
      compareNullableText(a.vendorName, b.vendorName),
    sortDirections: ['ascend', 'descend'],
    defaultSortOrder: 'ascend',
    width: 150,
  },
  {
    title: 'Collection',
    dataIndex: 'collection',
    key: 'collection',
    sorter: (a: { collection: string }, b: { collection: any }) => a.collection?.localeCompare(b.collection),
    sortDirections: ['ascend', 'descend'],
    width: 150,
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    width: 200,
    ellipsis: true,
  },
  {
    title: 'Photos',
    dataIndex: 'photos',
    key: 'photos',
    width: 120,
    render: (photos?: FixturePhoto[]) =>
      photos?.length ? (
        <div>
          {photos.map((photo, index) => (
            <div key={photo.id}>
              <Button
                type='link'
                style={{ padding: 0, height: 'auto' }}
                onClick={() => openFixturePhoto(photo.photoUrl)}
              >
                Photo {index + 1}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        '-'
      ),
  },
  {
    title: 'Active',
    dataIndex: 'active',
    key: 'active',
    width: 80,
    colType: 'checkbox',
    sorter: (a: { active: boolean }, b: { active: boolean }) => (a.active === b.active ? 0 : a.active ? -1 : 1),
    sortDirections: ['ascend', 'descend'],
    render: (value: boolean) => <CheckBoxCell value={value} />,
  },
  {
    title: 'Board Capacity',
    dataIndex: 'boardCapacity',
    key: 'boardCapacity',
    width: 120,
    sorter: (
      a: { boardCapacity?: number; board_capacity?: number; capacity?: number },
      b: { boardCapacity?: number; board_capacity?: number; capacity?: number }
    ) =>
      (a.boardCapacity ?? a.board_capacity ?? a.capacity ?? 0) -
      (b.boardCapacity ?? b.board_capacity ?? b.capacity ?? 0),
    sortDirections: ['ascend', 'descend'],
    render: (_: any, record: any) => record.boardCapacity ?? record.board_capacity ?? record.capacity,
  },
  {
    title: 'Storage Capacity',
    dataIndex: 'storageCapacity',
    key: 'storageCapacity',
    width: 130,
    sorter: (a: { storageCapacity: number }, b: { storageCapacity: number }) =>
      (a.storageCapacity || 0) - (b.storageCapacity || 0),
    sortDirections: ['ascend', 'descend'],
  },
  {
    title: 'Dimensions (W x H x D)',
    dataIndex: 'dimensions',
    key: 'dimensions',
    width: 150,
    render: (_: any, record: any) => {
      const { dimensionWidth, dimensionHeight, dimensionDepth } = record;
      if (!dimensionWidth && !dimensionHeight && !dimensionDepth) return '-';
      return `${dimensionWidth || 0} x ${dimensionHeight || 0} x ${dimensionDepth || 0}`;
    },
  },
  {
    title: 'Electricity',
    dataIndex: 'electricityRequired',
    key: 'electricityRequired',
    width: 80,
    colType: 'checkbox',
    render: (value: boolean) => <CheckBoxCell value={value} />,
  },
  {
    title: 'Lockable',
    dataIndex: 'lockable',
    key: 'lockable',
    width: 80,
    colType: 'checkbox',
    render: (value: boolean) => <CheckBoxCell value={value} />,
  },
  {
    title: 'Storage',
    dataIndex: 'storageDrawers',
    key: 'storageDrawers',
    width: 80,
    colType: 'checkbox',
    render: (value: boolean) => <CheckBoxCell value={value} />,
  },
  {
    title: 'Categories',
    dataIndex: 'categories',
    key: 'categories',
    width: 180,
    render: (_: any, record: any) => {
      const categories: string[] = [];
      if (record.catSpinner) categories.push('Spinner');
      if (record.catWheels) categories.push('Wheels');
      if (record.catFixed) categories.push('Fixed');
      if (record.catDoubleSided) categories.push('Double-Sided');
      if (record.catWindowUnit) categories.push('Window Unit');
      if (record.catWallUnit) categories.push('Wall Unit');
      if (record.catCabinet) categories.push('Cabinet');
      if (record.catShelving) categories.push('Shelving');
      return categories.length ? categories.join(', ') : '-';
    },
  },
];
